import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../config/firebase';
import HeroSlider from '../components/HeroSlider';
import { COUNTRIES } from '../data/countries';
import { CHAIRS } from '../data/chairs';
import { PROJECT_VIDEOS } from '../data/projectVideos';
import './Home.css';

// Counts are derived from the same data the other pages render, so the
// headline figures can never drift out of sync with the real catalogue.
const STATS = [
  { key: 'countries', value: COUNTRIES.length },
  { key: 'chairs', value: CHAIRS.length },
  { key: 'projects', value: PROJECT_VIDEOS.length }
];

// The showreel has no cover image, so it is not a showcase card.
const SHOWCASE = PROJECT_VIDEOS.filter((video) => video.thumb).slice(0, 4);

interface Article {
  title: string;
  content: string;
  description?: string;
  author: string;
  date: string;
}

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [articles, setArticles] = useState<{ [key: string]: Article }>({});


  // Salon applications (reserved for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _applications = [
    { name: "Akustik Duvar Yapımı", image: "https://static.wixstatic.com/media/e733f0_bc17f28857f4468fad5ebe1abebfb89df002.jpg/v1/fill/w_800,h_600,al_c,q_80,enc_avif,quality_auto/e733f0_bc17f28857f4468fad5ebe1abebfb89df002.jpg", description: "Profesyonel akustik duvar çözümleri" },
    { name: "Akustik Tavan Yapımı", image: "https://static.wixstatic.com/media/e733f0_8bfd13daf2ae4bdd9f3d5a6e592ad6e0f002.jpg/v1/fill/w_800,h_600,al_c,q_80,enc_avif,quality_auto/e733f0_8bfd13daf2ae4bdd9f3d5a6e592ad6e0f002.jpg", description: "Ses yalıtımı ve akustik tavan sistemleri" },
    { name: "Zemin Yükseltme", image: "https://static.wixstatic.com/media/e733f0_c49449c4307a45d7b34d2c43e940f78f~mv2.jpg/v1/fill/w_800,h_600,al_c,q_80,enc_avif,quality_auto/e733f0_c49449c4307a45d7b34d2c43e940f78f~mv2.jpg", description: "Modern zemin yükseltme sistemleri" },
    { name: "Salon Zemin Döşemeleri", image: "https://static.wixstatic.com/media/11062b_7ce95b72076f4b4a8a6a8116ef66e0e5~mv2.jpeg/v1/crop/x_0,y_12,w_6880,h_3220/fill/w_800,h_600,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Engineers%20and%20Businesspeople.jpeg", description: "Premium zemin kaplama çözümleri" },
    { name: "Salon Ses Sistemleri", image: "https://static.wixstatic.com/media/ee68f08f77854dac8c21bcc9ce161084.jpg/v1/crop/x_0,y_1115,w_5616,h_2628/fill/w_800,h_600,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Bricks%20and%20Mortar.jpg", description: "Profesyonel ses sistemleri" },
    { name: "Salon Aydınlatma Sistemleri", image: "https://static.wixstatic.com/media/e733f0_29d6314df0274c10aca3b3a61f229558~mv2.jpeg/v1/fill/w_800,h_600,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/e733f0_29d6314df0274c10aca3b3a61f229558~mv2.jpeg", description: "Modern aydınlatma çözümleri" },
    { name: "Salon Görüntü Sistemleri", image: "https://static.wixstatic.com/media/e733f0_c47188d2c1a44cd8a89afa91eec4ff89~mv2.jpeg/v1/fill/w_800,h_600,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/e733f0_c47188d2c1a44cd8a89afa91eec4ff89~mv2.jpeg", description: "Yüksek kaliteli görüntü sistemleri" },
    { name: "Kırım - Yıkım - Tadilat", image: "https://static.wixstatic.com/media/e733f0_ddf2da3ace784089bac0f553872f6140~mv2.jpeg/v1/fill/w_800,h_600,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/e733f0_ddf2da3ace784089bac0f553872f6140~mv2.jpeg", description: "Kapsamlı tadilat hizmetleri" }
  ];

  // Videos (reserved for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _videos = [
    {
      title: "Nish Global Mühendislik Tanıtım Videosu",
      description: "Nish Global Mühendislik",
      videoUrl: "https://repackager.wixmp.com/video.wixstatic.com/video/e733f0_6a6900aeafc14030a84f67e6c44d44fd/,360p,480p,/mp4/file.mp4.urlset/master.m3u8?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2aWRlb19pZCI6ImU3MzNmMF82YTY5MDBhZWFmYzE0MDMwYTg0ZjY3ZTZjNDRkNDRmZCIsImlzX2NsaXAiOmZhbHNlLCJpYXQiOjE3Njc3MTE2MDAsImV4cCI6MTc2Nzc5ODAwMCwiaXNzIjoiYXBwOnZvZCIsInN1YiI6InVzZXI6dm9kIn0.vymo_YAA2N33QSR7v76enxRA-HAyZD_ex9rT4A6003M",
      youtubeId: "6a6900aeafc14030a84f67e6c44d44fd", // Video ID'den türetilmiş - gerçek YouTube ID ile değiştirilebilir
      duration: "02:41"
    },
    {
      title: "Konferans Salonu Tasarımı",
      description: "Profesyonel Konferans Salonu Yapımı İçin Genel Tasarım Videosu",
      videoUrl: "https://repackager.wixmp.com/video.wixstatic.com/video/e733f0_37ad3eac6af14299b5f9eea721ea02ea/,360p,480p,/mp4/file.mp4.urlset/master.m3u8?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2aWRlb19pZCI6ImU3MzNmMF8zN2FkM2VhYzZhZjE0Mjk5YjVmOWVlYTcyMWVhMDJlYSIsImlzX2NsaXAiOmZhbHNlLCJpYXQiOjE3Njc3MTE2MDAsImV4cCI6MTc2Nzc5ODAwMCwiaXNzIjoiYXBwOnZvZCIsInN1YiI6InVzZXI6dm9kIn0.CGl46kdO8TfXrIcoSpz9kx3uiV_ouPs_fuMj2L5cRJg",
      duration: "02:41"
    },
    {
      title: "Çok Amaçlı Konferans Salonları",
      description: "Çok Amaçlı ve Profesyonel Salonlar İçin Tasarım ve Yapım Süreçleri",
      videoUrl: "https://repackager.wixmp.com/video.wixstatic.com/video/e733f0_f3404816689f43f2835578fc8ccacc63/,360p,480p,/mp4/file.mp4.urlset/master.m3u8?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2aWRlb19pZCI6ImU3MzNmMF9mMzQwNDgxNjY4OWY0M2YyODM1NTc4ZmM4Y2NhY2M2MyIsImlzX2NsaXAiOmZhbHNlLCJpYXQiOjE3Njc3MTE2MDAsImV4cCI6MTc2Nzc5ODAwMCwiaXNzIjoiYXBwOnZvZCIsInN1YiI6InVzZXI6dm9kIn0.Ipb5BgI9qQOP589A9J8r3ufJ3HeObJyPWEoWH72H8Bw",
      duration: "02:19"
    },
    {
      title: "Özel Okul Konferans Salonu Tasarımı",
      description: "Özel Okullar İçin Konferans Salonu Yapım ve Tasarımı",
      videoUrl: "https://repackager.wixmp.com/video.wixstatic.com/video/e733f0_0e38be0c6429438e80ee53ca97d356f9/,360p,480p,/mp4/file.mp4.urlset/master.m3u8?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2aWRlb19pZCI6ImU3MzNmMF8wZTM4YmUwYzY0Mjk0MzhlODBlZTUzY2E5N2QzNTZmOSIsImlzX2NsaXAiOmZhbHNlLCJpYXQiOjE3Njc3MTE2MDAsImV4cCI6MTc2Nzc5ODAwMCwiaXNzIjoiYXBwOnZvZCIsInN1YiI6InVzZXI6dm9kIn0.5Bs3bPjlT8G1Kuawm3I7hYVSolhJxG2qrfUDTnjPbQo",
      duration: "02:32"
    },
    {
      title: "Okul Konferans Salonu Tasarımı",
      description: "Eğitim kurumları için ekonomik konferans salonu tasarımları",
      videoUrl: "https://repackager.wixmp.com/video.wixstatic.com/video/e733f0_1dd767d25b3741a6abc809e840f889a4/,480p,/mp4/file.mp4.urlset/master.m3u8?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2aWRlb19pZCI6ImU3MzNmMF8xZGQ3NjdkMjViMzc0MWE2YWJjODA5ZTg0MGY4ODlhNCIsImlzX2NsaXAiOmZhbHNlLCJpYXQiOjE3Njc3MTE2MDAsImV4cCI6MTc2Nzc5ODAwMCwiaXNzIjoiYXBwOnZvZCIsInN1YiI6InVzZXI6dm9kIn0.KI8AAKpnTy-EviPtMwYg80SuogN2h-j1aTdQUeNfZPA",
      duration: "04:07"
    },
    {
      title: "Nish Global Mühendislik",
      description: "Nish Global Mühendislik",
      videoUrl: "https://repackager.wixmp.com/video.wixstatic.com/video/e733f0_a32611bff3d54ed7b9b07779618dd48f/,360p,480p,/mp4/file.mp4.urlset/master.m3u8?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2aWRlb19pZCI6ImU3MzNmMF9hMzI2MTFiZmYzZDU0ZWQ3YjliMDc3Nzk2MThkZDQ4ZiIsImlzX2NsaXAiOmZhbHNlLCJpYXQiOjE3Njc3MTE2MDAsImV4cCI6MTc2Nzc5ODAwMCwiaXNzIjoiYXBwOnZvZCIsInN1YiI6InVzZXI6dm9kIn0.OzxFyxST6qYAUgZ3ObpNPLUKEKBoHUzURpe8nTn3IMc",
      duration: "02:21"
    }
  ];

  // Partners
  const partners = [
    { name: "", image: "https://static.wixstatic.com/media/e733f0_45d1f34af2b9476e82d71da0a654a29a~mv2.png/v1/fill/w_278,h_268,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/schneider-electric-logo-png_seeklogo-123510.png" },
    { name: "", image: "https://static.wixstatic.com/media/e733f0_0a57a2e1240942768d60dccd215ca472~mv2.jpg/v1/fill/w_268,h_268,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/yamaha-logo-design-template-2a186d04b863b074df7c64226ab79f4b_screen.jpg" },
    { name: "", image: "https://static.wixstatic.com/media/e733f0_2aa606c74e9f4ad69967c5c981b46573~mv2.png/v1/fill/w_268,h_268,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Panasonic-Logo-1.png" },
    { name: "", image: "https://static.wixstatic.com/media/e733f0_2dd463f3e6884f2ea68852e09d45feb2~mv2.png/v1/fill/w_315,h_279,al_c,lg_1,q_85,enc_avif,quality_auto/images%20(1).png" },
    { name: "", image: "https://static.wixstatic.com/media/e733f0_7f7ebd01bffc453caf5aa3a2b675e66a~mv2.png/v1/fill/w_244,h_244,al_c,lg_1,q_85,enc_avif,quality_auto/knauf-logo.png" }
  ];

  // Certifications
  const certifications = [
    { name: "", image: "https://static.wixstatic.com/media/e733f0_1e9815db5b7a4e4bb47cb49cfe68e868~mv2.png/v1/fill/w_278,h_268,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/ISO_9001-2015_svg.png" },
    { name: "", image: "https://static.wixstatic.com/media/e733f0_e8b27e9ef8664ea59b32b0c0ab1ddf47~mv2.jpg/v1/fill/w_268,h_268,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/17.jpg" },
    { name: "", image: "https://static.wixstatic.com/media/e733f0_7c390dacb9e14665b28f1cde5e33f280~mv2.jpg/v1/fill/w_268,h_268,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/tse_logo-700x447.jpg" },
    { name: "", image: "https://static.wixstatic.com/media/e733f0_d38153b6193e4713883265cbdd65dd40~mv2.png/v1/fill/w_318,h_282,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/iso-45001.png" },
    { name: "", image: "https://static.wixstatic.com/media/e733f0_a0ff32a1466f44aabde458bf664dca02~mv2.png/v1/fill/w_268,h_268,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/iso-3834-2.png" }
  ];


  // Fetch articles from Firebase based on current language
  useEffect(() => {
    const currentLang = i18n.language === 'ru' ? 'ru' : i18n.language === 'ar' ? 'ar' : 'en';
    const articlesRef = ref(database, `technicalArticles/${currentLang}`);

    onValue(
      articlesRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setArticles(data);
        } else {
          setArticles({});
        }
      },
      (err) => {
        console.error('Error fetching articles:', err);
      }
    );

    return () => {
      off(articlesRef);
    };
  }, [i18n.language]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      let locale = 'en-US';
      if (i18n.language === 'ru') locale = 'ru-RU';
      else if (i18n.language === 'ar') locale = 'ar-SA';
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const articlesArray = Object.entries(articles).map(([id, article]) => ({
    id,
    ...article,
  }));

  // Sort by date (newest first) and take first 3
  articlesArray.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });

  const featuredArticles = articlesArray.slice(0, 3);

  return (
    <div className="home">
      {/* The hero is a slider, so its per-slide titles cannot be the page h1.
          This gives the document a single, stable top-level heading. */}
      <h1 className="u-sr-only">{t('home.pageTitle')}</h1>

      {/* Hero Video Slider */}
      <HeroSlider />

      {/* Modern Biz Kimiz Section */}
      {/* Proof band — real figures straight from the site's own data */}
      <section className="home-stats-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="home-stats-band"
          >
            <span className="home-stats-badge">{t('home.stats.badge')}</span>
            <dl className="home-stats-grid">
              {STATS.map((stat, index) => (
                <motion.div
                  key={stat.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                  className="home-stat"
                >
                  {/* Exact counts, not rounded claims — they come from the data. */}
                  <dt className="home-stat-value">{stat.value}</dt>
                  <dd className="home-stat-label">{t(`home.stats.${stat.key}`)}</dd>
                </motion.div>
              ))}
            </dl>
          </motion.div>
        </div>
      </section>

      <section className="modern-about-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="modern-about-content"
          >
            <div className="modern-about-header">
              <motion.span
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="modern-about-badge"
              >
                {t('home.about.badge')}
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="modern-about-title"
              >
                {t('home.about.title')}
              </motion.h2>
            </div>
            
            <div className="modern-about-grid">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="modern-about-text"
              >
                <p className="modern-about-intro" dangerouslySetInnerHTML={{ __html: t('home.about.intro') }} />
                <p className="modern-about-description">
                  {t('home.about.description')}
                </p>
                <div className="modern-about-stats">
                  <div className="stat-item">
                    <div className="stat-number">32+</div>
                    <div className="stat-label">{t('home.about.stats.countries')}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">1000+</div>
                    <div className="stat-label">{t('home.about.stats.projects')}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">18+</div>
                    <div className="stat-label">{t('home.about.stats.years')}</div>
                  </div>
                </div>
                <motion.a
                  href="/about"
                  whileHover={{ scale: 1.05, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  className="modern-about-link"
                >
                  {t('common.learnMore')}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </motion.a>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="modern-about-features"
              >
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <h3>{t('home.about.features.turnkey.title')}</h3>
                  <p>{t('home.about.features.turnkey.description')}</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                  </div>
                  <h3>{t('home.about.features.international.title')}</h3>
                  <p>{t('home.about.features.international.description')}</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h3>{t('home.about.features.quality.title')}</h3>
                  <p>{t('home.about.features.quality.description')}</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Split Layout Section */}
      <section className="split-layout-section">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="split-layout-container"
        >
          <div className="split-layout-image">
            <video 
              src="/Slider2.mp4" 
              autoPlay
              loop
              muted
              playsInline
              className="split-layout-video"
            />
            <div className="image-overlay"></div>
          </div>
          <div className="split-layout-content">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="content-wrapper"
            >
              <h3 className="content-tag">{t('home.splitLayout.tag')}</h3>
              <h2 className="content-title">
                {t('home.splitLayout.title')}
              </h2>
              <p className="content-description">
                {t('home.splitLayout.description')}
              </p>
              <motion.a
                href="https://wa.me/905408072727?text=Merhaba,%20daha%20fazla%20bilgi%20almak%20istiyorum."
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="explore-button"
              >
                {t('common.explore')}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </motion.a>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Technical Articles Section */}
      {articlesArray.length > 0 && (
        <section className="home-articles-section">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="section-header"
            >
              <h2 className="section-title">{t('home.articles.title')}</h2>
              <p className="section-description">
                {t('home.articles.description')}
              </p>
            </motion.div>

            <div className="home-articles-grid">
              {featuredArticles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="home-article-card"
                  onClick={() => navigate(`/technical-articles/${article.id}`)}
                >
                  <div className="home-article-content">
                    <div className="home-article-category">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <span>{t('home.articles.category')}</span>
                    </div>
                    <h3>{article.title}</h3>
                    {article.description && (
                      <p className="home-article-description">{article.description}</p>
                    )}
                    <div className="home-article-meta">
                      <div className="home-article-meta-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        <span>{formatDate(article.date)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="home-article-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="home-articles-cta"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/technical-articles')}
                className="view-all-articles-button"
              >
                {t('home.articles.viewAll')}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </motion.button>
            </motion.div>
          </div>
        </section>
      )}

      {/* Why Nish Global Section */}
      <section className="why-nish-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-header"
          >
            <h2 className="section-title">{t('home.whyNish.title')}</h2>
          </motion.div>
          <div className="why-nish-accordion">
            {[
              {
                title: t('home.whyNish.items.international.title'),
                description: t('home.whyNish.items.international.description')
              },
              {
                title: t('home.whyNish.items.partnership.title'),
                description: t('home.whyNish.items.partnership.description')
              },
              {
                title: t('home.whyNish.items.turnkey.title'),
                description: t('home.whyNish.items.turnkey.description')
              }
            ].map((item, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`why-nish-accordion-item ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                >
                  <div className="why-nish-accordion-header">
                    <h3>{item.title}</h3>
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="why-nish-accordion-icon"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </motion.div>
                  </div>
                  <motion.div
                    initial={false}
                    animate={{
                      height: isExpanded ? 'auto' : 0,
                      opacity: isExpanded ? 1 : 0,
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="why-nish-accordion-content"
                  >
                    <div className="why-nish-accordion-content-inner">
                      <p>{item.description}</p>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-header"
          >
            <h2 className="section-title">{t('home.partners.title')}</h2>
            <p className="section-description">
              {t('home.partners.description')}
            </p>
          </motion.div>
          
          <div className="partners-grid">
            {partners.map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="partner-card"
              >
                <div className="partner-logo-wrapper">
                  <img src={partner.image} alt={`Partner ${index + 1}`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="certifications-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-header"
          >
            <h2 className="section-title">{t('home.certifications.title')}</h2>
            <p className="section-description">
              {t('home.certifications.description')}
            </p>
          </motion.div>
          
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="certification-card"
              >
                <div className="certification-logo-wrapper">
                  <img src={cert.image} alt={`Certification ${index + 1}`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Selected work — the portfolio is the strongest argument, so it gets
          real estate on the home page rather than living only on /projects. */}
      <section className="home-showcase-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="section-header"
          >
            <span className="home-showcase-badge">{t('home.showcase.badge')}</span>
            <h2 className="section-title">{t('home.showcase.title')}</h2>
            <p className="section-description">{t('home.showcase.description')}</p>
          </motion.div>

          <div className="home-showcase-grid">
            {SHOWCASE.map((video, index) => (
              <motion.button
                key={video.key}
                type="button"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="home-showcase-card"
                onClick={() => navigate('/projects')}
              >
                <div className="home-showcase-media">
                  <img src={video.thumb} alt="" loading="lazy" />
                  <span className="home-showcase-play" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </div>
                <h3 className="home-showcase-title">
                  {t(`projects.videos.${video.key}.title`)}
                </h3>
                <p className="home-showcase-text">
                  {t(`projects.videos.${video.key}.description`)}
                </p>
              </motion.button>
            ))}
          </div>

          <div className="home-showcase-cta">
            <button type="button" className="home-cta-secondary" onClick={() => navigate('/projects')}>
              {t('home.showcase.cta')}
            </button>
          </div>
        </div>
      </section>

      {/* Closing CTA into the hall configurator */}
      <section className="home-final-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="home-final-cta-inner"
          >
            <h2>{t('home.finalCta.title')}</h2>
            <p>{t('home.finalCta.description')}</p>
            <div className="home-final-cta-actions">
              <button
                type="button"
                className="home-cta-primary"
                onClick={() => navigate('/design-your-hall')}
              >
                {t('home.finalCta.primary')}
              </button>
              <button
                type="button"
                className="home-cta-ghost"
                onClick={() => navigate('/contact')}
              >
                {t('home.finalCta.secondary')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
